<script setup>
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useProductStore } from '@/stores/productsStore';
import AddManagerApprovalRequest from '../Popups/AddManagerApprovalRequest.vue';
import CommentPopup from '../Popups/CommentPopup.vue';
import { computed, ref, watch } from 'vue';

const authStore = useAuthStore();
const orderStore = useOrderStore();
const store = useProductStore();

const showCommentPopup = ref(false);  // Control to show AddComment popup
const selectedItemForComment = ref(null);  // Track the selected overall discount for the comment
const backupOverallDiscount = ref(orderStore.state.overallDiscount);
const pendingApprovalDiscount = ref(null);  // Track if there is a pending approval for the overall discount
const isPopupCanceled = ref(false); // Track if the popup was canceled
const isCommentSubmitted = ref(false); // Track if the comment was successfully submitted

// Computed values for order total, GST, PST, and Discount
const tax1AllAmount = computed(() => orderStore.state.taxes[0] ? orderStore.getTotalTax1Amount : 0);
const tax2AllAmount = computed(() => orderStore.state.taxes[1] ? orderStore.getTotalTax2Amount : 0);
const orderTotal = computed(() => orderStore.getOrderTotal);

const effectiveTotal = computed(() => {
  const value = orderStore.isOverallDiscount.value ? orderStore.getGrandTotalForOverallDiscount : orderStore.getOrderTotal;
  return Math.round(value * 100) / 100;
});

watch(orderTotal, (newValue) => {
  orderStore.updateOrderTotal(newValue);
});

const userNow = store.users.find(user => user.FullName === authStore.currentUser);
const managerNow = store.users.find(user => user.FullName === authStore.managerUser);

const handleOverallDiscountInput = (event) => {
  console.log('Blur event...', authStore.currentUser, authStore.isManagerLoggedIn);
  const inputValue = event.target.value;
  const discountValue = parseFloat(inputValue);

  // Handle invalid cases
  if (isNaN(discountValue) || discountValue < 0) {
    resetDiscountInput();
    return;
  }

  
  // if (!userNow) {
  //   console.log('User not found');
  //   resetDiscountInput();
  //   return;
  // }

  // Update the input value immediately
  // orderStore.state.overallDiscount = discountValue;

  // Check discount authority
  if (discountValue <= userNow?.AllowedDicount) {
    orderStore.applyOverallDiscount(discountValue);
    return;
  }

  // Handle approval
  handleApprovalFlow(discountValue, event);
};

const resetDiscountInput = () => {
  orderStore.applyOverallDiscount(0);
  backupOverallDiscount.value = 0;
};

const handleApprovalFlow = (discountValue, event) => {
  // authStore.refreshAuthState();

  pendingApprovalDiscount.value = discountValue;
  selectedItemForComment.value = { 
    Name: 'Overall Discount', 
    discountPercentage: discountValue 
  };

  if (!authStore.isManagerLoggedIn) {
    authStore.toggleAddManagerApprovalRequest();
  } else if (authStore.isOnlyManagerLoggedIn && discountValue <= managerNow?.AllowedDicount) {
    showCommentPopup.value = false;
    orderStore.applyOverallDiscount(discountValue);
    console.log('Manager already logged in, discount approved withoutt comment', discountValue);
    return;
  } else {
    console.log('Manager logged in show comment popup checkee', discountValue);
    showCommentPopup.value = true;
  }
  
  // Revert display but keep actual value for approval
  event.target.value = backupOverallDiscount.value;
};

// Watch for manager login status and show comment popup if needed
watch(() => authStore.isManagerLoggedIn, (newVal) => {
  if (newVal && selectedItemForComment.value && pendingApprovalDiscount.value !== null) {
    showCommentPopup.value = true;
    isPopupCanceled.value = false;
    isCommentSubmitted.value = false;
  }
});

// Handle the comment submission and approval for the overall discount
const handleCommentSubmitted = (comment) => {
  if (comment.trim()) {
    // Apply the pending overall discount if a valid comment is provided
    if (pendingApprovalDiscount.value !== null) {
      orderStore.applyOverallDiscount(pendingApprovalDiscount.value);  
      backupOverallDiscount.value = pendingApprovalDiscount.value; 
      pendingApprovalDiscount.value = null;
      isCommentSubmitted.value = true;  
    }
  } else {
    // If no comment is provided, reset the discount to 0
    orderStore.state.overallDiscount = 0;
    isCommentSubmitted.value = false; 
  }

  showCommentPopup.value = false; 
};

// Handle the popup closure (triggered by cancel button)
const handleCommentPopupCancel = () => {
  isPopupCanceled.value = true; 
  showCommentPopup.value = false; 
};


// Disable overall discount if any item has a discount
const isOverallDiscountDisabled = computed(() => {
  return orderStore.state.orderItems.some(item => item.Discount > 0 || item.isIncludeTaxChecked);
});

function resetPendingApprovalDiscount () {
  console.log('cancel event received in parent ✅');
  pendingApprovalDiscount.value = null;
  selectedItemForComment.value = null;
}
</script>

<template> 
  <div v-if="(authStore.isUserLoggedIn || authStore.isManagerLoggedIn)" class="flex flex-col justify-end bg-white pb-2 mb-2 rounded-b-2xl"> 
    <AddManagerApprovalRequest @cancelPending="resetPendingApprovalDiscount"/>
    <CommentPopup 
      v-if="showCommentPopup" 
      :item="selectedItemForComment" 
      @commentSubmitted="handleCommentSubmitted" 
      @close="handleCommentPopupCancel" 
      @resetPending="resetPendingApprovalDiscount"
    />

    <div class="flex items-center justify-between rounded-2xl bg-[#f4f5f7] mx-3 my-2 py-4 px-3">
      <p>Overall Discount %</p>
      <input 
        type="number" 
        :value="orderStore.state.overallDiscount"
        @input="(e) => orderStore.state.overallDiscount = e.target.value"
        @blur="handleOverallDiscountInput"
        placeholder="Overall Discount (%)"
        :min="0"
        class="text-center w-20 rounded-lg transition-colors duration-300"
        :class="{
          'bg-purple-500 opacity-30 text-white cursor-not-allowed': isOverallDiscountDisabled, 
          'bg-white': !isOverallDiscountDisabled 
        }"
        :disabled="isOverallDiscountDisabled || orderStore.isManualOrder"
      />
    </div>

    <div>
    <div v-if="orderStore.state.taxes[0]" class="flex items-center justify-between rounded-2xl bg-[#f4f5f7] mx-3 my-2 py-4 px-3">
        <p>{{ orderStore.state.taxes[0].type }}<span>&#42;</span></p>
        <p>${{ tax1AllAmount }}</p>
      </div>
      <div v-if="orderStore.state.taxes[1]" class="flex items-center justify-between rounded-2xl bg-[#f4f5f7] mx-3 my-2 py-4 px-3">
        <p>{{ orderStore.state.taxes[1].type }}<span>&#42;</span></p>
        <p>${{ tax2AllAmount }}</p>
      </div>
    </div>

    <div class="flex items-center justify-between rounded-2xl bg-[#f4f5f7] mx-3 my-2 py-4 px-3">
      <p>Total:</p>
      <p>${{ effectiveTotal }}</p>
    </div>

  </div>
</template>



<style scoped>
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>

